import React, { FC, ReactNode } from "react";
import { Badge, Card, Icon, IconName, Input, Label, Text, Notification } from "@telia/teddy";
import { CopyButton } from "../../../components/atoms/CopyButton";
import { OrderContentHardwareItem, OrderContentSubscription, OrderDetails } from "../../../types/models";
import OrderDetailSection from "./OrderDetailSection";
import _capitalize from "lodash/capitalize";
import {
    formatMonthlyPrice,
    isPriceplanChanged,
    getBindingPeriodLabel,
    getBindingTypeLabel,
} from "./orderContentsUtils";
import "./OrderContentsSection.less";

interface OrderContentsSectionProps {
    order: OrderDetails;
}

interface ContentFieldProps {
    id: string;
    label: string;
    value?: string | null;
}

interface DetailRowProps {
    label: string;
    value?: string | null;
    masked?: boolean;
}

interface ContentItem {
    id: string;
    icon: IconName;
    title: string;
    price?: string;
    priceSubLabel?: string;
    lines: ReactNode[];
    extra?: ReactNode;
}

const HARDWARE_ICON: Record<OrderContentHardwareItem["type"], IconName> = {
    sim: "simcard",
    esim: "simcard",
    hardware: "smartphone",
};

const ContentField: FC<ContentFieldProps> = ({ id, label, value }) => {
    if (!value) return null;
    return (
        <div className="order-detail__contents-field">
            <Label htmlFor={id}>{label}</Label>
            <div className="order-detail__contents-field-row">
                <Input id={id} type="text" value={value} readOnly data-di-mask />
                <CopyButton
                    value={value}
                    ariaLabel={`Kopier ${label.toLowerCase()}`}
                    toastMessage={`${label} kopiert`}
                />
            </div>
        </div>
    );
};

const DetailRow: FC<DetailRowProps> = ({ label, value, masked }) => {
    if (!value) return null;
    return (
        <div className="order-detail__contents-detail-row">
            <Text className="order-detail__contents-detail-label">{label}:</Text>
            <Text data-di-mask={masked ? true : undefined}>{value}</Text>
        </div>
    );
};

const formatPortingDate = (timestamp: number): string => {
    const date = new Date(timestamp);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    return `${day}.${month}.${year} kl. ${hours}:${minutes}`;
};

const getSubscriptionTypeBadge = (subscription: OrderContentSubscription): ReactNode | null => {
    const type = subscription.subscriptionItemType?.toUpperCase();
    if (type === "PORTING") {
        const dateStr = subscription.estimatedPortingDate
            ? ` ${formatPortingDate(subscription.estimatedPortingDate)}`
            : "";
        return (
            <Badge key="type" variant="neutral" hideIcon className="order-detail__contents-type-badge">
                Portering <span className="order-detail__contents-type-badge-detail">(overføres{dateStr})</span>
            </Badge>
        );
    }
    if (type === "NEW") {
        return (
            <Badge key="type" variant="neutral" hideIcon className="order-detail__contents-type-badge">
                Nytt nummer
            </Badge>
        );
    }
    if (type === "CHANGE" && isPriceplanChanged(subscription.offeringName, subscription.previousOfferingName)) {
        return (
            <Badge key="type" variant="information" hideIcon className="order-detail__contents-type-badge">
                Endret abonnement{subscription.previousOfferingName ? ` fra ${subscription.previousOfferingName}` : ""}
            </Badge>
        );
    }
    return null;
};

const getBindingBadge = (subscription: OrderContentSubscription): ReactNode | null => {
    const bindingType = getBindingTypeLabel(subscription.offeringCode);
    if (!bindingType) return null;
    return (
        <Badge key="binding-type" variant="information" hideIcon className="order-detail__contents-type-badge">
            {_capitalize(bindingType)}
        </Badge>
    );
};

const subscriptionToContentItem = (subscription: OrderContentSubscription): ContentItem => {
    const productUserName = `${subscription.productUserFirstName ?? ""} ${
        subscription.productUserLastName ?? ""
    }`.trim();
    // Drop the trailing "binding" word since it's already covered by the "Binding" row label.
    const bindingPeriodValue = getBindingPeriodLabel(subscription.offeringCode)?.replace(/\s*binding$/i, "");
    const hasDiscount = !!subscription.monthlyDiscount && subscription.monthlyDiscount.amount > 0;
    const typeBadge = getSubscriptionTypeBadge(subscription);
    const bindingBadge = getBindingBadge(subscription);

    return {
        id: `subscription-${subscription.id}`,
        icon: "infinite",
        title: subscription.offeringName ?? "Abonnement",
        price: subscription.monthlyPrice ? formatMonthlyPrice(subscription.monthlyPrice.amount) : undefined,
        priceSubLabel:
            hasDiscount && subscription.monthlyPrice
                ? `ord. pris: ${formatMonthlyPrice(
                      subscription.monthlyPrice.amount + subscription.monthlyDiscount!.amount,
                  )}`
                : undefined,
        lines: [
            typeBadge,
            bindingBadge,
            <DetailRow key="binding" label="Binding" value={bindingPeriodValue} />,
            <DetailRow key="user" label="Bruker" value={productUserName} masked />,
            <DetailRow key="phone" label="Telefonnummer" value={subscription.phoneNumber} masked />,
        ].filter(Boolean),
        extra: subscription.imei ? (
            <ContentField id={`subscription-${subscription.id}-imei`} label="IMEI" value={subscription.imei} />
        ) : undefined,
    };
};

const hardwareItemToContentItem = (item: OrderContentHardwareItem): ContentItem => ({
    id: `hardware-${item.id}`,
    icon: HARDWARE_ICON[item.type],
    title: item.name ?? "Ukjent utstyr",
    lines: [
        item.materialId && <DetailRow key="material" label="Artikkelnr." value={item.materialId} />,
        item.quantity && item.quantity > 1 && (
            <DetailRow key="quantity" label="Antall" value={item.quantity.toString()} />
        ),
    ].filter(Boolean),
});

const buildContentItems = (subscriptions: OrderContentSubscription[]): ContentItem[] =>
    subscriptions.flatMap((subscription) => [
        subscriptionToContentItem(subscription),
        ...subscription.hardwareItems.map(hardwareItemToContentItem),
    ]);

const OrderContentsSection: FC<OrderContentsSectionProps> = ({ order }) => {
    const subscriptions = order.subscriptions ?? [];
    const items = buildContentItems(subscriptions);

    return (
        <OrderDetailSection title="Ordreinnhold">
            <Card variant="white" bordered className="order-detail__summary-card order-detail__contents-card">
                <Card.Content>
                    {items.length === 0 ? (
                        <Notification type="tile" variant="warning">
                            <Notification.Icon />
                            <Notification.Text>Ingen abonnementer funnet på denne ordren.</Notification.Text>
                        </Notification>
                    ) : (
                        <div className="order-detail__contents-list">
                            {items.map((item) => (
                                <div key={item.id} className="order-detail__contents-item">
                                    <div className="order-detail__contents-item-header">
                                        <Icon name={item.icon} size="lg" aria-hidden="true" focusable="false" />
                                        <Text
                                            variant="paragraph-100-bold"
                                            className="order-detail__contents-item-title"
                                        >
                                            {item.title}
                                        </Text>
                                        {item.price && (
                                            <div className="order-detail__contents-item-price">
                                                <Text variant="paragraph-100-bold">{item.price}</Text>
                                                {item.priceSubLabel && (
                                                    <Text variant="additional-100">{item.priceSubLabel}</Text>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                    {item.lines}
                                    {item.extra}
                                </div>
                            ))}
                        </div>
                    )}
                </Card.Content>
            </Card>
        </OrderDetailSection>
    );
};

export default OrderContentsSection;
